"use client";

/**
 * SimpleMobile - A simplified mobile view for the TTS component
 *
 * This component provides a wizard-like interface for mobile devices that:
 * - Shows VoicePickerTags content in a card-like layout
 * - Features larger play buttons for better mobile UX
 * - Includes a separate CTA to edit text (hides the large TipTap editor by default)
 * - Makes the layout cleaner and more mobile-friendly
 *
 * Usage:
 * <SimpleMobile
 *   voices={voices}
 *   selectedLanguage={selectedLanguage}
 *   onSelected={handleDemoPromptSelected}
 *   onEditText={() => setIsEditingText(true)}
 *   useV3Prompts={model === TTSModels.ELEVEN_V3}
 *   samplePrompts={samplePrompts}
 *   initialLanguage={initialLanguage}
 *   initialPrompts={initialPrompts}
 * />
 */

import type { Voice } from "@/app/_data/voices";
import type { IUseCasePromptWithId } from "@components/languages/types";

import { cn } from "@ui/lib/utils";

import type { SamplePrompt } from "./config";
import type { PlayingState } from "./constants";

import { VoicePickerTags } from "./components/VoicePickerTags";

interface SimpleMobileProps {
  activeSampleId?: string;
  className?: string;
  handlePlayPause: () => void;
  initialLanguage?: null | string;
  initialPrompts?: IUseCasePromptWithId[];
  onSelected: (prompt: IUseCasePromptWithId, voices: Voice[]) => void;
  playingState: PlayingState;
  samplePrompts?: SamplePrompt[] | null;
  selectedLanguage: string;
  useV3Prompts?: boolean;
  voices: Voice[];
}

export const SimpleMobile = ({
  voices,
  selectedLanguage,
  onSelected,
  className,
  useV3Prompts = false,
  samplePrompts = [],
  initialLanguage,
  initialPrompts,
  playingState,
  activeSampleId,
  handlePlayPause,
}: SimpleMobileProps) => {
  return (
    <div className={cn("flex h-full w-full flex-col p-16 pb-8", className)}>
      {/* Voice Cards using enhanced VoicePickerTags */}
      <VoicePickerTags
        activeSampleId={activeSampleId}
        className='flex-1 p-0'
        handlePlayPause={handlePlayPause}
        initialLanguage={initialLanguage}
        initialPrompts={initialPrompts}
        onSelected={onSelected}
        playingState={playingState}
        samplePrompts={samplePrompts}
        selectedLanguage={selectedLanguage}
        useV3Prompts={useV3Prompts}
        variant='cards'
        voices={voices}
      />
    </div>
  );
};
